/**
 * «Dal fatturato al margine» (Davide 03/11/2026): «non e' chiaro come stai
 * calcolando». Una cascata sola, dall'incassato con IVA al margine netto: ogni
 * riga ha l'importo e una frase che dice cosa e'. I numeri sono quelli del
 * payload e tornano: se un pezzo manca (backend vecchio) si scrive «—» e il
 * totale non si inventa.
 */

import type { BiDettagli } from "../biApi";
import { eur, n, quanti } from "../biTesti";
import { InfoTip } from "./InfoTip";
import { Riquadro } from "./Riquadro";

type Tipo = "voce" | "totale";

interface Riga {
  chiave: string;
  tipo: Tipo;
  segno?: "−" | "+" | "=";
  nome: string;
  /** ``null`` = dato assente: si scrive «—». */
  valore: number | null;
  spiega: string;
  /** Righe di dettaglio sotto l'importo (es. quanti ordini). */
  nota?: string;
}

const manca = (v: number | string | null | undefined) => v == null;

/** Le righe della cascata, con i totali calcolati dai pezzi del payload. */
export function righeCascata(d: BiDettagli): Riga[] {
  const e = d.economia;
  const p = d.costi_post_vendita;
  // Come la Testata: i bonifici seguono i filtri (costi_struttura sparisce coi filtri).
  const b = d.costi_bonifici?.total ?? d.costi_struttura?.utile.bonifici;
  const righe: Riga[] = [];

  const lordo = manca(e.gross_revenue) ? null : n(e.gross_revenue);
  const netto = n(e.net_revenue);
  righe.push({
    chiave: "lordo",
    tipo: "voce",
    nome: "Incassato con IVA",
    valore: lordo,
    spiega: "Quello che hanno pagato i clienti, IVA compresa, sugli ordini che hanno un netto.",
  });
  righe.push({
    chiave: "iva",
    tipo: "voce",
    segno: "−",
    nome: "IVA",
    valore: lordo == null ? null : lordo - netto,
    spiega: "Non e' nostra: la giriamo allo Stato.",
  });
  righe.push({
    chiave: "netto",
    tipo: "totale",
    segno: "=",
    nome: "Fatturato netto",
    valore: netto,
    spiega: "Quanto vendiamo davvero, senza IVA.",
  });

  const senzaCosti = netto - n(e.net_revenue_computable);
  if (Math.abs(senzaCosti) >= 0.005) {
    righe.push({
      chiave: "senza",
      tipo: "voce",
      segno: "−",
      nome: "Ordini senza margine",
      valore: senzaCosti,
      spiega: "Ordini a cui manca un costo: il margine non si calcola, quindi restano fuori.",
      nota: quanti(n(e.not_computable_count), "ordine", "ordini"),
    });
  }
  const calcolabile = n(e.net_revenue_computable);
  righe.push(
    {
      chiave: "prodotto",
      tipo: "voce",
      segno: "−",
      nome: "Prodotto e imballo",
      valore: n(e.product_cost) + n(e.packaging),
      spiega: "Quanto paghiamo al fornitore per la merce e per l'imballo.",
    },
    {
      chiave: "commissioni",
      tipo: "voce",
      segno: "−",
      nome: "Commissioni",
      valore: n(e.commission),
      spiega: "Quello che si tiene il marketplace su ogni vendita.",
    },
    {
      chiave: "spedizioni",
      tipo: "voce",
      segno: "−",
      nome: "Spedizioni",
      valore: n(e.shipping),
      spiega: "Il corriere per portare il pacco al cliente.",
    },
    {
      chiave: "margine",
      tipo: "totale",
      segno: "=",
      nome: "Margine ordini",
      valore: n(e.margin),
      spiega: `Cosa resta su ogni ordine venduto, prima di rimborsi e resi. Su ${eur(calcolabile)} di fatturato calcolabile.`,
    },
  );

  const rimborsi = p?.partial_refund_net;
  const resiSped = p ? n(p.return_outbound_cost) + n(p.return_labels_cost) : null;
  const campi = [
    p?.partial_refund_net,
    p?.return_goods_lost,
    p?.carrier_refund,
    p?.sold_supplier_refund,
  ];
  const completo = p != null && campi.every((c) => !manca(c));
  righe.push(
    {
      chiave: "rimborsi",
      tipo: "voce",
      segno: "−",
      nome: "Rimborsi parziali ai clienti",
      valore: manca(rimborsi) ? null : n(rimborsi),
      spiega:
        "Soldi restituiti al cliente che si e' tenuto il prodotto (senza IVA). Se il rimborso e' totale, l'ordine esce dal fatturato.",
      nota:
        p && !manca(p.partial_refund_count)
          ? quanti(n(p.partial_refund_count), "ordine", "ordini")
          : undefined,
    },
    {
      chiave: "resi_sped",
      tipo: "voce",
      segno: "−",
      nome: "Resi: spedizioni",
      valore: manca(resiSped) ? null : resiSped,
      spiega: "Spedizione di andata, che nessuno ci ridà, ed etichetta di ritorno.",
      nota: p ? quanti(n(p.return_orders), "reso", "resi") : undefined,
    },
    ...(p && n(p.return_customer_paid) > 0
      ? [
          {
            chiave: "resi_pagati",
            tipo: "voce" as const,
            segno: "+" as const,
            nome: "Resi: pagati dai clienti",
            valore: n(p.return_customer_paid),
            spiega: "Quanto i clienti ci pagano per il ritorno (tolto dal rimborso).",
          },
        ]
      : []),
    {
      chiave: "resi_merce",
      tipo: "voce",
      segno: "−",
      nome: "Resi: merce persa",
      valore: manca(p?.return_goods_lost) ? null : n(p?.return_goods_lost),
      spiega:
        "Prodotto e imballo pagati al fornitore su un reso che non si rivende (rotto o distrutto), meno quanto il fornitore ci rimborsa.",
      nota:
        p && !manca(p.return_goods_lost_orders)
          ? quanti(n(p.return_goods_lost_orders), "ordine", "ordini")
          : undefined,
    },
    {
      chiave: "sostituzioni",
      tipo: "voce",
      segno: "−",
      nome: "Sostituzioni",
      valore: p ? n(p.replacements_total) : null,
      spiega: "Pezzi di ricambio ed etichette per rimandare al cliente la merce rotta o mancante.",
      nota: p ? quanti(n(p.replacement_orders), "ordine", "ordini") : undefined,
    },
    {
      chiave: "corriere",
      tipo: "voce",
      segno: "+",
      nome: "Rimborsi del corriere",
      valore: manca(p?.carrier_refund) ? null : n(p?.carrier_refund),
      spiega: "Il corriere ci rimborsa un pacco perso o rotto (pagina Reclami).",
      nota:
        p && !manca(p.carrier_refund_count)
          ? quanti(n(p.carrier_refund_count), "reclamo", "reclami")
          : undefined,
    },
    {
      chiave: "note",
      tipo: "voce",
      segno: "+",
      nome: "Note di credito dei fornitori",
      valore: manca(p?.sold_supplier_refund) ? null : n(p?.sold_supplier_refund),
      spiega: "Il fornitore ci ridà dei soldi su ordini che abbiamo venduto (senza IVA).",
      nota:
        p && !manca(p.sold_supplier_refund_count)
          ? quanti(n(p.sold_supplier_refund_count), "nota", "note")
          : undefined,
    },
  );
  if (!manca(b) && n(b) > 0) {
    righe.push({
      chiave: "bonifici",
      tipo: "voce",
      segno: "−",
      nome: "Bonifici ai fornitori",
      valore: n(b),
      spiega: "Commissioni di banca sui bonifici a Itamoby e Ityhome (0,50 € l'uno).",
    });
  }

  const finale =
    completo && p
      ? n(e.margin) -
        n(p.partial_refund_net) -
        (n(p.returns_total) - n(p.return_goods_lost)) -
        n(p.return_goods_lost) -
        n(p.replacements_total) +
        n(p.carrier_refund) +
        n(p.sold_supplier_refund) -
        n(b)
      : null;
  righe.push({
    chiave: "finale",
    tipo: "totale",
    segno: "=",
    nome: "Margine netto",
    valore: finale,
    spiega:
      finale == null
        ? "Non si puo' calcolare: al backend mancano i dati di rimborsi e resi."
        : "Quello che resta dalle vendite, prima di pubblicità e costi di struttura.",
  });
  return righe;
}

function Voce({ r }: { r: Riga }) {
  const totale = r.tipo === "totale";
  const negativo = r.valore != null && r.valore < 0;
  return (
    <li
      className={`grid grid-cols-[1.25rem_minmax(0,1fr)_auto] items-baseline gap-x-2 py-2.5 ${
        totale ? "border-t border-line-2 bg-paper-2/60 px-2 max-md:-mx-1" : ""
      }`}
    >
      <span className="text-center font-display text-[16px] font-bold" aria-hidden="true">
        {r.segno ?? ""}
      </span>
      <span className="min-w-0">
        <span className={`text-[14px] ${totale ? "font-bold" : "font-semibold"}`}>
          {r.nome}
          {r.nota && <span className="ml-2 text-[12px] font-semibold">{r.nota}</span>}
        </span>
        <span className="mt-0.5 block text-[12.5px] leading-snug">{r.spiega}</span>
      </span>
      <span
        data-riga={r.chiave}
        className={`text-right font-display font-bold tabular-nums ${
          totale ? "text-[20px]" : "text-[16px]"
        } ${negativo && totale ? "text-red" : ""}`}
      >
        {r.valore == null ? "—" : eur(r.valore, 2)}
      </span>
    </li>
  );
}

export function Cascata({ d }: { d: BiDettagli }) {
  const righe = righeCascata(d);
  return (
    <Riquadro
      titolo="Dal fatturato al margine"
      spiegazione={{
        titolo: "Dal fatturato al margine",
        testo:
          "Ogni riga e' un passo: si parte da quanto hanno pagato i clienti e si tolgono, una alla volta, IVA, costi, rimborsi, resi e sostituzioni. Si aggiunge quello che ci ridanno corriere e fornitori. Importi senza IVA, tranne la prima riga.",
        incerto:
          n(d.economia.estimate_count) > 0
            ? `${n(d.economia.estimate_count)} ordini hanno un costo stimato.`
            : undefined,
      }}
    >
      <ol className="divide-y divide-line">
        {righe.map((r) => (
          <Voce key={r.chiave} r={r} />
        ))}
      </ol>
      <div className="mt-4 rounded-[12px] bg-paper-2 px-3.5 py-3 text-[12.5px] leading-snug">
        <p className="flex items-center gap-1.5 font-bold">
          Cosa e' fuori dal fatturato
          <InfoTip
            spiegazione={{
              titolo: "Cosa e' fuori",
              testo:
                "Gli ordini annullati, quelli rimborsati per intero e quelli resi non contano come vendite: non sono nel fatturato. Di loro restano solo i costi di reso, che stanno nelle righe «Resi».",
            }}
          />
        </p>
        <p className="mt-1">
          Ordini annullati, ordini rimborsati per intero e ordini resi: non sono nel fatturato.
          Contano solo i loro costi, nelle righe dei resi.
        </p>
      </div>
    </Riquadro>
  );
}
